import { useMemo } from "react";
import { useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { rangeForPreset } from "../../lib/dashboardMetrics";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import {
  encodeReviewFeedSearch,
  reviewFeedFilters,
} from "../../lib/reviewFeed";
import { useTypedNavigate, useTypedSearch } from "../../lib/typedRouter";
import { useListQuery } from "../../lib/useListQuery";
import {
  canonicalWorkingScopeSearch,
  useWorkingScope,
  useResolvedWorkingTeamId,
  type WorkingScopeSearch,
} from "../../lib/workingScope";
import {
  parseReviewListSearch,
  type ReviewListSearch,
  type ReviewSummary,
} from "./reviewListData";
import {
  parseReviewMarkerSearch,
  type ReviewMarkerSearch,
} from "./reviewMarkerData";
import { ReviewMarkerView } from "./ReviewMarkerView";
import { ReviewMarkerScorecard } from "./ReviewMarkerScorecard";

type Answer = components["schemas"]["AnswerResponse"];

export function ReviewMarker() {
  const { markerId = "" } = useParams({ strict: false });
  const { agentId } = useWorkingScope();
  const teamId = useResolvedWorkingTeamId();
  const search = useTypedSearch(parseReviewMarkerSearch);
  const navigate = useTypedNavigate<WorkingScopeSearch & ReviewMarkerSearch>();
  const listNavigate = useTypedNavigate<
    WorkingScopeSearch & ReviewListSearch
  >();
  const transcriptNavigate = useTypedNavigate<
    WorkingScopeSearch & {
      label?: string;
      feed: string;
    }
  >();
  const range = search.range ?? "30d";
  const timeWindow = useMemo(() => rangeForPreset(range), [range]);
  const enabled = Boolean(agentId || teamId);
  const marker = useQuery({
    queryKey: queryKeys.markers.detail(markerId),
    queryFn: async ({ signal }) => {
      const { data, error } = await apiClient.GET("/v1/markers/{id}", {
        signal,
        params: { path: { id: markerId } },
      });
      if (error) throw new ApiRequestError(error);
      return data;
    },
  });
  const summaryRequest = {
    time_range: timeWindow,
    agent_id: agentId ?? null,
    team_id: agentId ? null : (teamId ?? null),
    agent_version_id: search.version ?? null,
    batch_id: null,
    source: null,
  };
  const summary = useQuery({
    enabled,
    queryKey: queryKeys.markers.reviewSummary(summaryRequest),
    queryFn: async ({ signal }) => {
      const items: ReviewSummary[] = [];
      let token: string | undefined;
      do {
        const { data, error } = await apiClient.POST(
          "/v1/markers:review-summary",
          {
            signal,
            body: {
              ...summaryRequest,
              page_size: 200,
              page_token: token ?? null,
            },
          },
        );
        if (error) throw new ApiRequestError(error);
        items.push(...data.items);
        token = data.next_page_token ?? undefined;
      } while (token);
      return items;
    },
  });
  const counts = summary.data?.find((row) => row.marker_id === markerId);
  function feedFor(tab: ReviewMarkerSearch["tab"], cell?: string) {
    const feed = new URLSearchParams(
      encodeReviewFeedSearch({
        agentId,
        teamId,
        marker_id: markerId,
        agent_version_id: search.version,
        created_after: timeWindow.start,
        created_before: timeWindow.end,
        needs_review: tab === "needs_review" ? true : "any",
        reviewed: tab === "reviewed" ? true : undefined,
        sort: tab === "needs_review" ? "oldest" : "newest",
      }),
    );
    feed.set("range", range);
    feed.set("tab", tab);
    if (search.version) feed.set("version", search.version);
    if (cell) feed.set("cell", cell);
    return feed.toString();
  }
  const feed = feedFor(search.tab);
  const filters = reviewFeedFilters(feed);
  const calls = useListQuery("/v1/answers", {
    enabled,
    params: filters,
    queryKey: queryKeys.answers.list(filters),
  });
  const needsFilters = reviewFeedFilters(feedFor("needs_review"));
  const needs = useListQuery("/v1/answers", {
    enabled,
    params: needsFilters,
    queryKey: queryKeys.answers.list(needsFilters),
  });
  function open(transcriptId: string, label: string | undefined, feed: string) {
    void transcriptNavigate({
      to: "/transcripts/$id",
      params: { id: transcriptId },
      search: {
        agent: agentId,
        team: agentId ? undefined : teamId,
        label,
        feed,
      },
    });
  }
  function openCall(answer: Answer, selectedFeed = feed) {
    open(answer.transcript_id, answer.id, selectedFeed);
  }
  return (
    <ReviewMarkerView
      markerName={marker.data?.current_revision.display_name}
      markerPending={marker.isPending}
      error={
        (marker.isError ? requestErrorDescription(marker.error) : undefined) ??
        (summary.isError
          ? requestErrorDescription(summary.error)
          : undefined) ??
        needs.errorMessage
      }
      scopeLabel={`${range} · ${search.version ? "Version filtered" : "All versions"}`}
      canStart={Boolean(needs.items[0]) && !needs.isPending}
      onStart={() => {
        if (needs.items[0]) openCall(needs.items[0], feedFor("needs_review"));
      }}
      onBack={() => {
        void listNavigate({
          to: "/review",
          search: (previous) => ({
            ...canonicalWorkingScopeSearch(previous),
            ...parseReviewListSearch({ ...previous }),
          }),
        });
      }}
      tab={search.tab}
      onTab={(tab) => {
        void navigate({
          search: (previous) => ({
            ...previous,
            ...parseReviewMarkerSearch({ ...previous, tab }),
          }),
        });
      }}
      needsReviewCount={counts?.needs_review_count}
      reviewedCount={counts?.reviewed_count}
      calls={{ ...calls, isPending: enabled && calls.isPending }}
      onOpen={openCall}
      scorecard={
        <ReviewMarkerScorecard
          summary={counts}
          enabled={enabled}
          markerId={markerId}
          feed={feed}
          cell={search.cell}
          onCell={(cell) => {
            void navigate({ search: (previous) => ({ ...previous, cell }) });
          }}
          onOpen={(row) => {
            open(
              row.transcript_id,
              row.machine_answer_id ?? row.human_answer_id ?? undefined,
              feedFor(
                "reviewed",
                search.cell ?? `${row.human_verdict}-${row.machine_verdict}`,
              ),
            );
          }}
        />
      }
    />
  );
}
